/**
 * The note field row, shared by Read's expand-in-place panel and Cards' note dock.
 *
 * Those two edit the same six fields of the same notetype, and until now each carried its own copy
 * of this markup — identical but for a `min-w-0` one of them had and the other did not. The array
 * below is the same list both panels send to `updateNote`, so a field added here reaches both.
 */

import { useId } from "react";

import type { NoteDetail, UpdateNoteBody } from "@/lib/note";

/**
 * The editable fields, in the order they are shown.
 *
 * `multiline` is not a styling preference — it says the field legitimately holds newlines.
 * `english` does: the engine writes multi-sense definitions as `"1) study\n2) diligence\n"`
 * (`format_english_and_pos`, server-side), and a single-line input rendered that as one run-on
 * string and silently flattened a multi-line paste on the way back.
 *
 * **`sentence` does too, and it was the same bug with worse consequences** (日本-453). A capture of a
 * multi-line dialogue box really does hold newlines, and 215 notes carry one; the DOM `input.value`
 * setter strips CR/LF, so the break was invisible here *and* destroyed by any unrelated edit to the
 * field — a repair you could not see was needed and could not make. 日本-453 stops new captures
 * carrying a *wrap*-induced break, which is the bulk of them, but a genuine two-line sentence stays
 * two lines and this is where it is read and edited.
 *
 * `partOfSpeech` is deliberately not on the list — the engine writes it comma-joined on one line,
 * so a newline there would contradict the field. `extra` is arguable and is held back only for
 * scope; it is one boolean if you want it.
 *
 * `optional` says the notetype may not carry the field at all. Read it through `noteFieldsFor`
 * rather than iterating this array directly.
 */
/**
 * Field names that legitimately hold NEWLINES, wherever they appear.
 *
 * **A rule about the field, not about the notetype**, which is what lets one set serve every one of
 * them — a `Sentence` holds newlines on `Genki (Apoc)` and on `Names (Apoc)` alike, and a future
 * notetype naming a field `Sentence` means the same thing by it.
 *
 * `multiline` is not a styling preference. `English` really does hold them: the engine writes
 * multi-sense definitions as `"1) study\n2) diligence\n"` (`format_english_and_pos`,
 * server-side), and a single-line input rendered that as one run-on string and silently flattened
 * a multi-line paste on the way back.
 *
 * **`Sentence` does too, and it was the same bug with worse consequences** (日本-453). A capture of
 * a multi-line dialogue box really does hold newlines, and 215 notes carry one; the DOM
 * `input.value` setter strips CR/LF, so the break was invisible here *and* destroyed by any
 * unrelated edit to the field — a repair you could not see was needed and could not make.
 *
 * **`Example`, `Formation` and `Notes` were on this list and went with 日本-519.** They were
 * `Grammar (Apoc)`'s, and `Example` was the sharpest case of all — it held every example the book
 * gives, one per line, so a single-line input would have collapsed five sentences into one on the
 * first save. `Meaning` and `Usage` stay: they are ordinary field names that a notetype this app
 * does not know may well carry, and the rule above is about the field.
 *
 * `Part of Speech` is deliberately absent — the engine writes it comma-joined on one line, so a
 * newline there would contradict the field. `Extra` is arguable and is held back only for scope.
 */
const MULTILINE_FIELDS = new Set([
  "English",
  "Sentence",
  "DictionaryDivergence",
  "Meaning",
  "Usage",
]);

/**
 * Where a field's row label is not its Anki name.
 *
 * One entry, and it earns it: `Part of Speech` at full length pushes the control off a 390px row.
 * Everything else reads as itself, which is the point — these are Anki's names and stay English,
 * because they are what you would type into Anki's own browser.
 */
const FIELD_LABELS: Record<string, string> = { "Part of Speech": "POS" };

export interface NoteFieldSpec {
  /** The ANKI field name. Also the draft key and what goes up in `fields`. */
  key: string;
  label: string;
  multiline?: boolean;
}

/**
 * The fields this particular note's editor offers, in order.
 *
 * **The SERVER decides which and in what order** (日本-498) — `NoteDetail.fields`, which is
 * notetype-driven. This function only decides how each row is drawn. That inversion is worth more
 * than the notetype that motivated it: the list used to be `Genki (Apoc)`'s, spelled here, so a
 * note on any other notetype rendered six blank inputs over content it did not have and the panel
 * refused to save. `Grammar (Apoc)` was the case that exposed it and 日本-519 removed that
 * notetype; the inversion stays, and a seventh notetype now costs no client change at all.
 *
 * It also retires the `optional` special case. `DictionaryDivergence` is added to `Genki (Apoc)`
 * by hand in Anki, and the server now serves a field only when the note actually has it — so a
 * collection without it simply gets no row, rather than every client needing to know to filter on
 * `null`.
 */
export function noteFieldsFor(note: Pick<NoteDetail, "fields">): NoteFieldSpec[] {
  return (note.fields ?? []).map(({ name }) => ({
    key: name,
    label: FIELD_LABELS[name] ?? name,
    multiline: MULTILINE_FIELDS.has(name),
  }));
}

/**
 * The draft a freshly loaded note starts from — every offered field, by its Anki name.
 *
 * **Here rather than spelled out in each panel**, because both used to seed by indexing
 * `NoteDetail` with the row's key — which only worked while every key was a named property of that
 * interface. The values come from `fields` now, so the seed and the render read the same list and
 * cannot disagree about which fields a note has.
 */
export function noteDraft(note: Pick<NoteDetail, "fields">): Record<string, string> {
  return Object.fromEntries((note.fields ?? []).map(({ name, value }) => [name, value ?? ""]));
}

/**
 * The PUT body for a saved draft — the single place that decides which keys go up.
 *
 * Both panels used to spell this literal out, identically, with nothing testing either copy. That
 * was survivable while the list was fixed; it stopped being so the moment one key became
 * conditional, and it would have stopped again the moment a second notetype had its own fields.
 *
 * It sends `fields`, keyed by ANKI name, rather than the camelCase keys: that is the only shape
 * that can carry a notetype whose fields have no named key on `UpdateNoteBody`. The server
 * validates every name against the live note and rejects the whole save on one it does not have,
 * which is what keeps a generic map from being a way to write anything anywhere.
 *
 * **Tags are deliberately NOT in here.** `TagChips` writes them the moment they change, so a draft
 * save that also sent `note.tags` would be sending a snapshot taken before that write — and the API
 * applies exactly what it is handed. Ctrl+Enter landing while a tag write is still in flight would
 * put the removed tag back, with the panel showing it gone; the same body also reverts a tag any
 * other device or job added since the panel loaded. Omitting the key means "leave the tags alone",
 * which is the only thing a text-field save has any business saying about them.
 */
export function noteUpdateBody(
  draft: Record<string, string>,
  note: Pick<NoteDetail, "fields">,
): UpdateNoteBody {
  const fields: Record<string, string> = {};
  for (const { key } of noteFieldsFor(note)) fields[key] = draft[key] ?? "";
  return { fields };
}

/**
 * One class string for both controls, so the textarea and the inputs above and below it cannot
 * drift apart. `min-w-0` is load-bearing rather than tidy: a flex item defaults to `min-width:
 * auto` and refuses to shrink below its min-content width, and a textarea's intrinsic width comes
 * from its default `cols` — without it the row pushes the layout wider than the viewport at phone
 * widths, which the design forbids at any width.
 */
const CONTROL =
  "min-w-0 flex-1 border-b border-line-soft bg-transparent pb-[5px] text-body text-ink outline-none focus:border-accent";

export interface NoteFieldRowProps {
  label: string;
  value: string;
  /** Renders a textarea that grows with its content. See `NOTE_FIELDS`. */
  multiline?: boolean;
  onChange: (next: string) => void;
}

export function NoteFieldRow({ label, value, multiline, onChange }: NoteFieldRowProps) {
  // `useId` rather than the field key: the two panels can be mounted at once, and duplicate ids
  // would point one panel's label at the other panel's control.
  const id = useId();

  return (
    <label
      htmlFor={id}
      // A textarea has `overflow: auto`, and an overflow box's baseline is synthesized from its
      // bottom margin edge — so under `items-baseline` the label would sink a line lower with
      // every line typed. The grid wrapper below is an ordinary visible block, which puts the
      // baseline back on the first line and lets both variants share this alignment.
      className="flex items-baseline gap-[10px]"
    >
      {/* **96px, and the 8px of it is the gap rather than slack.** Measured rather than guessed:
          the label column was a fixed 78px and `SENTENCE` — `Genki (Apoc)`'s longest — inks at
          exactly 78, so it sat flush at the limit with no gap and nobody noticed. `Grammar (Apoc)`
          brought `FORMATION` at 88, which overflowed the box and ran straight into its own value
          (`FORMATIONVて しまう`). **That notetype went with 日本-519 and the 96 stays**: what it
          measured is the widest label this app CAN show, not the widest one it happens to today.

          `pr-2` is what makes 96 correct rather than arbitrary: the content box is then 88, which
          is exactly the widest label this app can show, and the padding is the space before the
          control. It costs every row 18px of control width, which is real on a 390px phone and is
          the honest price of a label that does not collide. */}
      <span className="w-[96px] shrink-0 pr-2 text-field-label font-semibold uppercase tracking-[.06em] text-ink-4">
        {label}
      </span>
      {multiline ? (
        // Auto-grow with a CSS sizer rather than measuring `scrollHeight` in an effect. The split
        // is user-draggable, so the field's width — and therefore how the text wraps — changes
        // without the value changing; a value-keyed effect would leave the height stale until the
        // next keystroke, and fixing that means a ResizeObserver per field. The grid track sizes
        // to the hidden span and reflows on its own.
        //
        // `field-sizing: content` collapses all of this to one declaration, and deletes the span
        // the day Safari ships it.
        <div className="grid min-w-0 flex-1">
          <span
            aria-hidden
            // The cap lives here, on the sizer, because the sizer is what the grid track measures.
            // On the textarea it does nothing — the track still grows to the full text and leaves
            // dead space under a capped control (89px of it on 本's eleven senses). On the wrapper
            // it only clips: the track is still 257px, so the textarea stretches to 257px and has
            // its last lines cut off with no scrollbar. Clamped here, the track stops at the cap,
            // the textarea stretches to exactly that, and its own overflow-y takes over.
            //
            // `border-b` is not decoration — it is invisible. The box decorations have to match the
            // control's exactly, because `box-sizing: border-box` means the track this span sizes is
            // the textarea's BORDER box: without the same 1px, the textarea's content box comes out
            // a pixel shorter than the text it is holding and every multi-line value overflows by
            // one pixel, which reads as a scrollbar that will not go away.
            className="invisible col-start-1 row-start-1 max-h-[var(--field-max-h)] overflow-hidden whitespace-pre-wrap break-words border-b border-transparent pb-[5px] text-body"
          >
            {/* The trailing space is what gives the field's own trailing newline a line to sit on,
                so the box does not end one line short of the text it is holding. */}
            {value + " "}
          </span>
          <textarea
            id={id}
            rows={1}
            value={value}
            onChange={(e) => onChange(e.target.value)}
            className={`${CONTROL} col-start-1 row-start-1 resize-none overflow-y-auto`}
          />
        </div>
      ) : (
        <input id={id} value={value} onChange={(e) => onChange(e.target.value)} className={CONTROL} />
      )}
    </label>
  );
}
